import { McpServer } from '@modelcontextprotocol/sdk/server/mcp.js';
import { z } from 'zod';
import { listEntities } from '@stackwright-pro/cli-data-explorer';
import { jsonCoerce } from '../coerce.js';

/**
 * Register dashboard generation MCP tools.
 * Tools for building pages that display live API data.
 */
export function registerDashboardTools(server: McpServer): void {
  // Tool: Generate dashboard page configuration
  server.tool(
    'stackwright_pro_generate_dashboard',
    'Generate a dashboard page configuration for displaying API data. ' +
      'Creates YAML content for a Stackwright page with grid, metric_card, data_table, and collection_list content types. ' +
      'Use this after stackwright_pro_generate_filter to create pages for your API collections.',
    {
      entities: jsonCoerce(z.array(z.string())).describe('Entity slugs to include in dashboard'),
      layout: z.enum(['grid', 'table', 'mixed']).optional().describe('Dashboard layout style'),
      pageTitle: z.string().optional().describe('Page title'),
      specPath: z.string().optional().describe('Path to OpenAPI spec for entity details'),
    },
    async ({ entities, layout = 'mixed', pageTitle, specPath }) => {
      // Get entity details if spec provided
      let entityDetails: any[] = [];
      if (specPath) {
        const result = listEntities({ specPath });
        if (result.success) {
          entityDetails = result.entities.filter((e) => entities.includes(e.slug));
        }
      }

      const title =
        pageTitle || entities.map((e) => e.charAt(0).toUpperCase() + e.slice(1)).join(' & ');

      const yamlLines = [
        '# Dashboard page configuration',
        '# Generated by Stackwright Pro MCP',
        '',
        'content:',
        '  meta:',
        `    title: "${title} | Dashboard"`,
        `    description: "Live data from API"`,
        '',
        '  content_items:',
      ];

      // Add metric cards in a grid for each entity
      if (layout === 'grid' || layout === 'mixed') {
        for (const slug of entities) {
          yamlLines.push(`    - type: grid`);
          yamlLines.push(`      columns: 4`);
          yamlLines.push(`      items:`);
          yamlLines.push(`        - type: metric_card`);
          yamlLines.push(`          label: "Total"`);
          yamlLines.push(`          value: {{ ${slug}.count }}`);
          yamlLines.push(`          icon: Database`);
          yamlLines.push(`        - type: metric_card`);
          yamlLines.push(`          label: "Active"`);
          yamlLines.push(`          value: 0`);
          yamlLines.push(`          icon: CheckCircle`);
          yamlLines.push('');
        }
      }

      // Add collection listing for each entity
      yamlLines.push(`    - type: collection_list`);
      yamlLines.push(`      label: ${entities[0]}-listing`);
      yamlLines.push(`      collection: ${entities[0]}`);
      yamlLines.push(`      showFilters: true`);
      yamlLines.push(`      showSearch: true`);
      const defaultCols = ['id', 'name', 'status', 'created_at'];
      yamlLines.push(
        `      columns: ${
          entityDetails[0]?.fields
            ?.slice(0, 4)
            .map((f: any) => f.name)
            .join(', ') || defaultCols.join(', ')
        }`
      );
      yamlLines.push('');

      if (layout === 'table') {
        yamlLines.push(`    - type: data_table`);
        yamlLines.push(`      label: ${entities[0]}-table`);
        yamlLines.push(`      collection: ${entities[0]}`);
        yamlLines.push(`      columns:`);
        yamlLines.push(`        - field: id`);
        yamlLines.push(`          header: ID`);
        yamlLines.push(`          sortable: true`);
        yamlLines.push(`        - field: name`);
        yamlLines.push(`          header: Name`);
        yamlLines.push(`          sortable: true`);
        yamlLines.push(`        - field: status`);
        yamlLines.push(`          header: Status`);
        yamlLines.push(`          type: badge`);
      }

      const yaml = yamlLines.join('\n');

      return {
        content: [
          {
            type: 'text' as const,
            text: `📊 Generated Dashboard for: ${entities.join(', ')}\n\nLayout: ${layout}\n\n\`\`\`yaml\n${yaml}\n\`\`\`\n\n💡 Add this to pages/dashboard/content.yml and validate with stackwright_validate_pages.`,
          },
        ],
      };
    }
  );

  // Tool: Generate detail page
  server.tool(
    'stackwright_pro_generate_detail_page',
    'Generate a detail view page for a single API entity. ' +
      'Creates YAML content for displaying all fields of an individual record. ' +
      'Use this to create detail pages that complement collection listing pages.',
    {
      entity: z.string().describe('Entity slug (e.g., "equipment")'),
      slugField: z.string().optional().describe('Field to use as URL slug (default: "id")'),
      specPath: z.string().optional().describe('Path to OpenAPI spec for field details'),
    },
    async ({ entity, slugField = 'id', specPath }) => {
      // Get field details if spec provided
      let fields: any[] = [];
      if (specPath) {
        const result = listEntities({ specPath });
        if (result.success) {
          const ent = result.entities.find((e) => e.slug === entity);
          if (ent) {
            fields = ent.fields;
          }
        }
      }

      const entityName = entity.charAt(0).toUpperCase() + entity.slice(1);

      const yamlLines = [
        `# Detail page for ${entityName}`,
        '# Generated by Stackwright Pro MCP',
        '',
        'content:',
        '  meta:',
        `    title: "${entityName} Details | {{ ${entity}.${slugField} }}"`,
        '',
        '  content_items:',
        '    - type: text_block',
        '      label: "detail-header"',
        '      heading:',
        `        text: "{{ ${entity}.${slugField} }}"`,
        '        textSize: "h1"',
        '      textBlocks:',
        `        - text: "Details for this ${entity}"`,
        '          textSize: "body1"',
        '',
        '    - type: grid',
        '      label: "detail-fields"',
        '      columns:',
      ];

      // Add field displays as text_block items within grid columns
      const displayFields =
        fields.length > 0
          ? fields.slice(0, 8)
          : [
              { name: slugField, type: 'string' },
              { name: 'created_at', type: 'datetime' },
              { name: 'updated_at', type: 'datetime' },
            ];

      // Split fields into two columns
      const mid = Math.ceil(displayFields.length / 2);
      const col1 = displayFields.slice(0, mid);
      const col2 = displayFields.slice(mid);

      for (const [colIndex, colFields] of [col1, col2].entries()) {
        yamlLines.push('        - width: 1');
        yamlLines.push('          content_items:');
        for (const field of colFields) {
          const label = field.name
            .replace(/_/g, ' ')
            .replace(/\b\w/g, (l: string) => l.toUpperCase());
          yamlLines.push('            - type: text_block');
          yamlLines.push(`              label: "${field.name}-field"`);
          yamlLines.push('              heading:');
          yamlLines.push(`                text: "${label}"`);
          yamlLines.push('                textSize: "h4"');
          yamlLines.push('              textBlocks:');
          yamlLines.push(`                - text: "{{ ${entity}.${field.name} }}"`);
          yamlLines.push('                  textSize: "body1"');
        }
      }

      // Back navigation
      yamlLines.push('');
      yamlLines.push('    - type: action_bar');
      yamlLines.push('      actions:');
      yamlLines.push(`        - label: "<- Back to ${entityName}"`);
      yamlLines.push('          action: navigate');
      yamlLines.push(`          href: "/${entity}"`);
      yamlLines.push('          style: secondary');

      const yaml = yamlLines.join('\n');

      return {
        content: [
          {
            type: 'text' as const,
            text: `📄 Generated Detail Page for: ${entity}\n\n\`\`\`yaml\n${yaml}\n\`\`\`\n\n💡 Add this to pages/${entity}/[${slugField}]/content.yml and validate.`,
          },
        ],
      };
    }
  );
}
